import { useTranslate } from 'core/hooks/useTranslate';
import { DialogImage, Icon, Image } from 'ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import consumptionsv2 from '../../../../store/viewer';

const ImageViewer = ({ guid, contentUrl, contentName, contentDescription, userGuid, measureProgress, canOpenImageDialog }) => {
  const { t } = useTranslate();

  const [isConsumed, setIsConsumed] = useState(false);
  const [consumptionGuid, setConsumptionGuid] = useState('');
  const [consumptionSessionGuid, setConsumptionSessionGuid] = useState('');
  const [showDialogImage, setShowDialogImage] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);
  const [showFallback, setShowFallback] = useState(!contentUrl);
  const imageRef = useRef();

  useEffect(() => {
    if (contentUrl) {
      setShowFallback(false);
      setHasImageError(false);
    } else {
      setShowFallback(true);
    }
  }, [contentUrl]);

  function onShowImage() {
    if (canOpenImageDialog && !showFallback) {
      setShowDialogImage(true);
    }
  }

  function onClose() {
    setShowDialogImage(false);
  }

  useEffect(() => {
    if (!contentUrl) return;

    const fileExtension = contentUrl.split('.').pop().split('?')[0].toLowerCase();

    if (fileExtension === 'heic' || fileExtension === 'heif') {
      const timeoutId = setTimeout(() => {
        if (!isConsumed && !hasImageError) {
          setShowFallback(true);
        }
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [contentUrl, isConsumed, hasImageError]);

  const handleImageError = () => {
    setHasImageError(true);
    setShowFallback(true);
  };

  const handleImageLoad = () => {
    setIsConsumed(true);
    setHasImageError(false);
    setShowFallback(false);
  };

  const getDownloadUrl = () => {
    return contentUrl;
  };

  const getFileName = () => {
    const fileName = contentUrl.split('/').pop().split('?')[0];
    return fileName || contentName || 'image';
  };

  useEffect(() => {
    if (guid && measureProgress) {
      consumptionsv2(
        {
          type: 'INIT_SESION_CONSUMPTION',
          params: {
            guid: null,
            person_guid: userGuid,
            content_guid: guid,
            status: 'inProgress',
            type: 'image',
            progress: 0,
            progress_ends: 1,
          },
        },
        (result) => {
          setConsumptionGuid(result.guid);
          setConsumptionSessionGuid(result.session_guid);
        }
      );
    }
  }, [guid]);

  useEffect(() => {
    if (isConsumed && consumptionGuid && consumptionGuid.length > 0 && consumptionSessionGuid && consumptionSessionGuid.length > 0) {
      consumptionsv2(
        {
          type: 'UPDATE_CONSUMPTION',
          params: {
            id: 0,
            consumption_guid: consumptionGuid,
            content_guid: guid,
            session_guid: consumptionSessionGuid,
            progress_start: 1,
            progress_end: 1,
            started_at: Date.now() - 1000,
            ended_at: Date.now(),
          },
        },
        () => {}
      );
    }
  }, [isConsumed, consumptionGuid, consumptionSessionGuid]);

  return (
    <>
      <div
        className="preview-content__image"
        onClick={onShowImage}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onShowImage();
          }
        }}
        role="button"
        tabIndex={0}
      >
        {showFallback && (
          <div className="preview-content__image-fallback">
            <div className="preview-content__error-message">
              <p>{contentUrl ? t('messages:The format of this image is not supported in your browser') : t('messages:This image is not available')}</p>
              {contentUrl && (
                <a href={getDownloadUrl()} download={getFileName()} className="preview-content__download-link" aria-label={t('buttons:You can download it here')}>
                  <Icon name="download" /> <span>{t('buttons:You can download it here')}</span>
                </a>
              )}
            </div>
          </div>
        )}

        {contentUrl && (
          <Image
            ref={imageRef}
            className="preview-content__img"
            src={contentUrl}
            onLoad={handleImageLoad}
            onError={handleImageError}
            alt={t('alt:Preview of contentType', { contentType: contentName || 'image' })}
            style={{ display: showFallback ? 'none' : 'block' }}
          />
        )}
      </div>
      <div className="preview-content__description">{contentDescription}</div>
      <DialogImage alt={t('alt:Preview of contentType', { contentType: contentName || 'image' })} isOpen={showDialogImage} onClose={onClose} src={contentUrl} />
    </>
  );
};

ImageViewer.propTypes = {
  canOpenImageDialog: PropTypes.bool,
  contentDescription: PropTypes.string,
  contentName: PropTypes.string,
  contentUrl: PropTypes.string,
  guid: PropTypes.string,
  measureProgress: PropTypes.bool,
  userGuid: PropTypes.string,
};

ImageViewer.defaultProps = {
  canOpenImageDialog: true,
  measureProgress: true,
};

export default ImageViewer;
